const hexToRgba = (hex: string, alpha: number) => {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r},${g},${b},${alpha})`;
};

const teal = '#6dc15b'; // PRIMARY COLOR OF THE APP — single source of truth

const colors = {
  // Brand colors
  navy: '#1e293b',    // PRIMARY DARK COLOR OF THE APP
  teal,

  // Backgrounds
  paper: '#f8fafc',
  card: '#ffffff',
  white: '#ffffff',

  // Text
  textMuted: '#64748b',
  muted: '#94a3b8',

  // Borders
  borderLight: '#f1f5f9',
  border: '#e2e8f0',
  gray300: '#cbd5e1',

  // Semantic colors
  success: '#10b981',
  successText: '#16a34a',
  error: '#ef4444',
  errorText: '#dc2626',
  warning: '#f59e0b',
  warningText: '#78350f',
  info: '#3b82f6',
  infoText: '#1d4ed8',

  // Teal variants
  tealBg: hexToRgba(teal, 0.08),
  tealBorder: hexToRgba(teal, 0.2),

  // Connie gold — used for gentle error/attention states
  connieGold: '#d4a017',
  connieGoldText: '#92700c',

  // Alpha variants
  whiteAlpha10: 'rgba(255,255,255,0.1)',
  whiteAlpha50: 'rgba(255,255,255,0.5)',
  whiteAlpha60: 'rgba(255,255,255,0.6)',
} as const;

export const theme = {
  fontFamily: {
    base: '"Inter", ui-sans-serif, system-ui, sans-serif',
    display: '"Outfit", sans-serif',
    serif: '"Cormorant Garamond", serif',
  },

  colors,

  gradients: {
    primary: `linear-gradient(135deg, ${colors.navy} 0%, ${colors.teal} 100%)`,
    success: `linear-gradient(90deg, ${colors.success}, ${colors.teal})`,
    weather: `linear-gradient(135deg, ${colors.info} 0%, ${colors.infoText} 100%)`,
    map: `linear-gradient(135deg, ${colors.gray300} 0%, ${colors.muted} 100%)`,
  },

  shadows: {
    card: '0 1px 3px rgba(0,0,0,0.06), 0 1px 2px rgba(0,0,0,0.04)',
    header: '0 4px 20px rgba(20,184,166,0.15)',
    button: '0 8px 25px rgba(20,184,166,0.3)',
    tool: '0 4px 15px rgba(20,184,166,0.25)',
    xl: '0 20px 60px rgba(0,0,0,0.1)',
  },

  spacing: {
    xs: '0.25rem',
    sm: '0.5rem',
    md: '0.75rem',
    lg: '1rem',
    xl: '1.25rem',
    '2xl': '1.5rem',
    '3xl': '2rem',
    '4xl': '3rem',
  },

  borderRadius: {
    sm: '6px',
    md: '12px',
    lg: '16px',
    xl: '24px',
    '2xl': '2rem',
    '3xl': '3rem',
    full: '9999px',
  },

  fontSize: {
    xs: '0.7rem',
    sm: '0.75rem',
    base: '0.875rem',
    md: '1rem',
    lg: '1.1rem',
    xl: '1.25rem',
    '2xl': '1.5rem',
    '3xl': '2rem',
    '4xl': '2.5rem',
  },

  fontWeight: {
    light: 300,
    normal: 400,
    medium: 500,
    semibold: 600,
    bold: 700,
  },

  transitions: {
    fast: '0.15s',
    normal: '0.3s',
    slow: '0.5s',
  },

  breakpoints: {
    mobile: '640px',
    tablet: '768px',
    desktop: '1024px',
    wide: '1600px',
  },

  // Centralised form-step scale — change here to affect all steps
  form: {
    headingSize: '2.4rem',
    subtitleSize: '1.2rem',
    buttonFontSize: '1.05rem',
    buttonPadding: '0.9rem 2rem',
    avatarSize: 128,
    // Mobile overrides
    mobile: {
      headingSize: '1.6rem',
      subtitleSize: '0.95rem',
      buttonFontSize: '0.9rem',
      buttonPadding: '0.75rem 1.5rem',
      avatarSize: 80,
    },
  },
} as const;

export type Theme = typeof theme;
